// ApiKeyWizard.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { cleanString, downloadFile } from '../apiUtils';

// Services
import { createBaseServiceAccount } from 'services/api/ApiAccessService';

// CSS
import './ApiKeyWizard.css';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';
import { toast } from 'react-toastify';

const API_KEY_WIZARD_STEPS = Object.freeze({
    NAME: 'name',
    ACCESS: 'access',
    REVIEW: 'review',
    REVEAL: 'reveal',
});

const TYPE_PERMISSION_OPTIONS = Object.freeze([
    { id: 'none', label: 'No access' },
    { id: 'read', label: 'View only' },
    { id: 'write', label: 'View and change data' },
]);

function normalizeTypePermissionLevel(value) {
    const normalized = cleanString(value).toLowerCase();
    if (normalized === 'read' || normalized === 'write') {
        return normalized;
    }
    return 'none';
}

function buildDefaultTypePermissionMap(typeRows) {
    const rows = Array.isArray(typeRows) ? typeRows : [];
    return rows.reduce((acc, row) => {
        const typeId = cleanString(row?.typeId).toLowerCase();
        if (!typeId) return acc;
        acc[typeId] = 'none';
        return acc;
    }, {});
}

function normalizeTypePermissionMap(rawMap, typeRows) {
    const defaults = buildDefaultTypePermissionMap(typeRows);
    const map = rawMap && typeof rawMap === 'object' && !Array.isArray(rawMap) ? rawMap : {};

    return Object.keys(defaults).reduce((acc, typeId) => {
        acc[typeId] = normalizeTypePermissionLevel(map[typeId]);
        return acc;
    }, {});
}

function summarizeTypePermissions(typePermissionMap, typeRows) {
    const rows = Array.isArray(typeRows) ? typeRows : [];
    const permissionMap = typePermissionMap && typeof typePermissionMap === 'object' ? typePermissionMap : {};

    let writeCount = 0;
    let readCount = 0;
    let noneCount = 0;

    rows.forEach((row) => {
        const level = normalizeTypePermissionLevel(permissionMap[row.typeId]);
        if (level === 'write') {
            writeCount += 1;
            return;
        }
        if (level === 'read') {
            readCount += 1;
            return;
        }
        noneCount += 1;
    });

    return {
        writeCount,
        readCount,
        noneCount,
        compact: `${writeCount} write • ${readCount} read • ${noneCount} none`,
    };
}

/**
 * ApiKeyWizard
 *
 * Modal wizard used to create an API key and reveal its token once.
 *
 * @returns {ReactNode} The rendered component.
 */
const ApiKeyWizard = ({
    isOpen,
    setOpen,
    baseId,
    canManageCredentials,
    typeRows,
    onCreated,
}) => {

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- STATE ----- //

    const [step, setStep] = useState(API_KEY_WIZARD_STEPS.NAME);
    const [draftName, setDraftName] = useState('');
    const [typePermissions, setTypePermissions] = useState(() => buildDefaultTypePermissionMap(typeRows));
    const [errorMessage, setErrorMessage] = useState('');
    const [busyActionKey, setBusyActionKey] = useState('');
    const [revealedToken, setRevealedToken] = useState('');
    const [revealedTokenAccountId, setRevealedTokenAccountId] = useState('');

    // ----- MEMOS ----- //

    const typePermissionSummary = useMemo(() => {
        return summarizeTypePermissions(typePermissions, typeRows);
    }, [typePermissions, typeRows]);

    const reviewRows = useMemo(() => {
        const rows = Array.isArray(typeRows) ? typeRows : [];
        return rows.filter((row) => normalizeTypePermissionLevel(typePermissions[row.typeId]) !== 'none');
    }, [typePermissions, typeRows]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!isOpen) return;

        setStep(API_KEY_WIZARD_STEPS.NAME);
        setDraftName('');
        setTypePermissions(buildDefaultTypePermissionMap(typeRows));
        setErrorMessage('');
        setBusyActionKey('');
        setRevealedToken('');
        setRevealedTokenAccountId('');
    }, [isOpen, typeRows]);

    useEffect(() => {
        setTypePermissions((prev) => {
            const next = normalizeTypePermissionMap(prev, typeRows);
            const prevSerialized = JSON.stringify(prev || {});
            const nextSerialized = JSON.stringify(next || {});
            return prevSerialized === nextSerialized ? prev : next;
        });
    }, [typeRows]);

    // ----- HANDLERS ----- //

    const handleClose = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    const handleSetTypePermission = useCallback((typeId, nextLevel) => {
        const cleanTypeId = cleanString(typeId).toLowerCase();
        if (!cleanTypeId) return;

        setTypePermissions((prev) => ({
            ...prev,
            [cleanTypeId]: normalizeTypePermissionLevel(nextLevel),
        }));
    }, []);

    const handleApplyTypePermissionToAll = useCallback((nextLevel) => {
        const normalizedLevel = normalizeTypePermissionLevel(nextLevel);
        setTypePermissions((prev) => {
            const next = { ...prev };
            (Array.isArray(typeRows) ? typeRows : []).forEach((row) => {
                next[row.typeId] = normalizedLevel;
            });
            return next;
        });
    }, [typeRows]);

    const handleContinueName = useCallback(() => {
        if (!cleanString(draftName)) {
            setErrorMessage('API key name is required.');
            return;
        }

        setErrorMessage('');
        setStep(API_KEY_WIZARD_STEPS.ACCESS);
    }, [draftName]);

    const handleContinueAccess = useCallback(() => {
        setErrorMessage('');
        setStep(API_KEY_WIZARD_STEPS.REVIEW);
    }, []);

    const handleCreateApiKey = useCallback(async () => {
        if (!baseId || !canManageCredentials) return;

        const name = cleanString(draftName);
        if (!name) {
            setErrorMessage('API key name is required.');
            return;
        }

        const permissions = {
            types: normalizeTypePermissionMap(typePermissions, typeRows),
            schema: {
                types: 'none',
            },
            baseMeta: {
                read: true,
            },
        };

        setBusyActionKey('create-api-key');
        setErrorMessage('');

        try {
            const response = await createBaseServiceAccount({
                baseId,
                name,
                role: 'member',
                permissions,
            });

            const accountId = cleanString(response?.serviceAccount?.id);
            const token = cleanString(response?.token);

            setRevealedToken(token);
            setRevealedTokenAccountId(accountId);
            setStep(API_KEY_WIZARD_STEPS.REVEAL);
            toast.success('API key created.');

            if (typeof onCreated === 'function') {
                await onCreated(response);
            }
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to create API key.');
        } finally {
            setBusyActionKey('');
        }
    }, [canManageCredentials, draftName, onCreated, typePermissions, typeRows, baseId]);

    const handleCopyRevealedToken = useCallback(async () => {
        if (!revealedToken) return;

        try {
            await navigator.clipboard.writeText(revealedToken);
            toast.success('API key copied.');
        } catch (error) {
            toast.error(error?.message || 'Could not copy API key.');
        }
    }, [revealedToken]);

    const handleDownloadRevealedToken = useCallback(() => {
        if (!revealedToken || !revealedTokenAccountId || !baseId) return;

        const fileName = `base-${baseId}-api-key-${revealedTokenAccountId}.txt`;
        downloadFile(fileName, `${revealedToken}\n`, 'text/plain;charset=utf-8');
    }, [revealedToken, revealedTokenAccountId, baseId]);

    // ----- MEMOS ----- //

    const modalTitle = useMemo(() => {
        if (step === API_KEY_WIZARD_STEPS.ACCESS) return 'API Key Type Access';
        if (step === API_KEY_WIZARD_STEPS.REVIEW) return 'Review API Key';
        if (step === API_KEY_WIZARD_STEPS.REVEAL) return 'Save API Key (Shown Once)';
        return 'Add API Key';
    }, [step]);

    // ----- RENDER ----- //

    return (
        <Modal
          title={modalTitle}
          isOpen={isOpen}
          width={isMobile ? '100%' : 760}
          maxHeight="90vh"
          onClose={handleClose}
        >
            <div className="api-key-wizard-container">
                {errorMessage && (
                    <div className="api-keys-error">{errorMessage}</div>
                )}

                {/* BODY */}
                <div className="api-key-wizard-body">

                    {/* SECTION */}
                    {step === API_KEY_WIZARD_STEPS.NAME && (

                        <section className="api-key-wizard-section">
                            <div
                              className="api-keys-subtitle"
                              style={{
                                color: theme.foregroundMuted,
                              }}
                            >
                                Name this key based on the integration that will use it.
                            </div>

                            <div className="api-keys-form-grid">
                                <TextInput
                                  value={draftName}
                                  placeholder="Example: Task Sync"
                                  disabled={!canManageCredentials}
                                  onChange={(event) => setDraftName(event.target.value)}
                                />
                            </div>

                            <div className="api-keys-actions">
                                <Button
                                  label="Cancel"
                                  enabled={busyActionKey !== 'create-api-key'}
                                  onClick={handleClose}
                                />
                                <Button
                                  label="Continue"
                                  enabled={canManageCredentials && busyActionKey !== 'create-api-key'}
                                  onClick={handleContinueName}
                                />
                            </div>
                        </section>
                    )}

                    {/* SECTION */}
                    {step === API_KEY_WIZARD_STEPS.ACCESS && (

                        <section className="api-key-wizard-section">
                            <div
                              className="api-keys-subtitle"
                              style={{
                                color: theme.foregroundMuted,
                              }}
                            >
                                Set each type to No access, View only, or View and change data.
                            </div>

                            <div className="api-keys-actions">
                                <Button
                                  label="Set All To No Access"
                                  enabled={canManageCredentials && typeRows.length > 0}
                                  onClick={() => handleApplyTypePermissionToAll('none')}
                                />
                                <Button
                                  label="Set All To View Only"
                                  enabled={canManageCredentials && typeRows.length > 0}
                                  onClick={() => handleApplyTypePermissionToAll('read')}
                                />
                            </div>

                            {typeRows.length === 0 ? (
                                <div
                                  className="api-keys-empty"
                                  style={{
                                    color: theme.foregroundMuted,
                                  }}
                                >
                                    No base types found. Create types first or continue with no type access.
                                </div>
                            ) : (
                                <div className="api-keys-type-list">
                                    {typeRows.map((row) => (
                                        <div
                                          className="api-keys-type-row"
                                          style={{
                                            borderColor: theme.backgroundMuted,
                                          }}
                                          key={row.typeId}
                                        >
                                            <div
                                              className="api-keys-type-label"
                                              style={{
                                                color: theme.foreground,
                                              }}
                                            >{row.label}</div>
                                            <SelectInput
                                              value={normalizeTypePermissionLevel(typePermissions[row.typeId])}
                                              disabled={!canManageCredentials}
                                              options={TYPE_PERMISSION_OPTIONS.map((option) => ({
                                                value: option.id,
                                                label: option.label,
                                              }))}
                                              onChange={(event) => handleSetTypePermission(row.typeId, event.target.value)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="api-keys-actions">
                                <Button
                                  label="Back"
                                  enabled={busyActionKey !== 'create-api-key'}
                                  onClick={() => setStep(API_KEY_WIZARD_STEPS.NAME)}
                                />
                                <Button
                                  label="Continue"
                                  enabled={canManageCredentials && busyActionKey !== 'create-api-key'}
                                  onClick={handleContinueAccess}
                                />
                            </div>
                        </section>
                    )}

                    {/* SECTION */}
                    {step === API_KEY_WIZARD_STEPS.REVIEW && (

                        <section className="api-key-wizard-section">
                            <div
                              className="api-keys-subtitle"
                              style={{
                                color: theme.foregroundMuted,
                              }}
                            >
                                API key name: {cleanString(draftName)}
                            </div>
                            <div
                              className="api-keys-subtitle"
                              style={{
                                color: theme.foregroundMuted,
                              }}
                            >
                                Type permissions: {typePermissionSummary.compact}
                            </div>

                            {reviewRows.length > 0 && (
                                <div className="api-keys-review-list">
                                    {reviewRows.map((row) => (
                                        <div
                                          className="api-keys-review-row"
                                          style={{
                                            borderColor: theme.backgroundMuted,
                                            color: theme.foregroundMuted,
                                          }}
                                          key={row.typeId}
                                        >
                                            <span>{row.label}</span>
                                            <span>{normalizeTypePermissionLevel(typePermissions[row.typeId])}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="api-keys-actions">
                                <Button
                                  label="Back"
                                  enabled={busyActionKey !== 'create-api-key'}
                                  onClick={() => setStep(API_KEY_WIZARD_STEPS.ACCESS)}
                                />
                                <Button
                                  label="Create API Key"
                                  enabled={canManageCredentials && busyActionKey !== 'create-api-key'}
                                  onClick={handleCreateApiKey}
                                />
                            </div>
                        </section>
                    )}

                    {/* SECTION */}
                    {step === API_KEY_WIZARD_STEPS.REVEAL && (

                        <section className="api-key-wizard-section">
                            {revealedToken ? (
                                <>
                                    <textarea
                                      className="api-keys-token"
                                      style={{
                                            backgroundColor: theme.input,
                                            borderColor: theme.backgroundMuted,
                                            color: theme.foreground,
                                        }}
                                      value={revealedToken}
                                      readOnly
                                    />

                                    <div className="api-keys-actions">
                                        <Button
                                          label="Copy API Key"
                                          iconKey="content_copy"
                                          onClick={handleCopyRevealedToken}
                                        />

                                        <Button
                                          label="Download API Key"
                                          iconKey="download"
                                          onClick={handleDownloadRevealedToken}
                                        />
                                    </div>

                                    <div
                                      className="api-keys-subtitle"
                                      style={{
                                        color: theme.foregroundMuted,
                                      }}
                                    >
                                        You cannot view this full API key again later.
                                    </div>

                                    <div className="api-keys-actions">
                                        <Button
                                          label="Done"
                                          enabled={true}
                                          onClick={handleClose}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div
                                  className="api-keys-empty"
                                  style={{
                                    color: theme.foregroundMuted,
                                  }}
                                >
                                    Could not reveal API key. Please try creating it again.
                                </div>
                            )}
                        </section>
                    )}
                </div>
            </div>
        </Modal>
    );
};

// Dev Tools
ApiKeyWizard.displayName = 'ApiKeyWizard';

export default ApiKeyWizard;
